import React from 'react';
import { TrendingUp, FileText, Calculator, ExternalLink } from 'lucide-react';
import { colors } from '../../styles/colors';
import { formatEuro } from '../../utils/formatters';

/**
 * Composant de comparaison CA généré vs Montant facturé
 * Affiche 3 KPIs: CA total, Total facturé, Reste à facturer
 * Cards cliquables pour ouvrir la modale de détail
 */
export default function FacturationComparison({ caAnnuel = 0, totalFacture = 0, onShowDetail }) {

  // Reste à facturer
  const resteAFacturer = caAnnuel - totalFacture;

  const clickable = !!onShowDetail;

  // Style pour les cartes
  const cardStyle = {
    flex: 1,
    padding: '1rem 1.25rem',
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    minWidth: '200px',
    position: 'relative',
    cursor: clickable ? 'pointer' : 'default',
    transition: 'all 200ms'
  };

  const iconContainerStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: '500',
    marginBottom: '0.25rem'
  };

  const valueStyle = {
    fontSize: '1.25rem',
    fontWeight: '700'
  };

  const handleHover = (e) => {
    if (!clickable) return;
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
  };

  const handleLeave = (e) => {
    if (!clickable) return;
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  };

  return (
    <div style={{
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap'
    }}>
      {/* CA Total Généré */}
      <div
        style={{
          ...cardStyle,
          backgroundColor: colors.background.lightBlue,
          border: `1.5px solid ${colors.primary.teal}`
        }}
        onClick={onShowDetail}
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
      >
        <div style={{
          ...iconContainerStyle,
          backgroundColor: `${colors.primary.teal}20`
        }}>
          <TrendingUp size={20} color={colors.primary.teal} />
        </div>
        <div>
          <div style={{ ...labelStyle, color: colors.primary.gray }}>
            CA Généré
          </div>
          <div style={{ ...valueStyle, color: colors.primary.teal }}>
            {formatEuro(caAnnuel)}
          </div>
        </div>
        {clickable && (
          <ExternalLink size={16} style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', color: colors.primary.teal }} />
        )}
      </div>

      {/* Total Facturé */}
      <div
        style={{
          ...cardStyle,
          backgroundColor: colors.background.lightYellow,
          border: `1.5px solid ${colors.primary.gold}`
        }}
        onClick={onShowDetail}
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
      >
        <div style={{
          ...iconContainerStyle,
          backgroundColor: `${colors.primary.gold}20`
        }}>
          <FileText size={20} color={colors.primary.gold} />
        </div>
        <div>
          <div style={{ ...labelStyle, color: colors.text.brown }}>
            Total Facturé
          </div>
          <div style={{ ...valueStyle, color: colors.text.goldDark }}>
            {formatEuro(totalFacture)}
          </div>
        </div>
        {clickable && (
          <ExternalLink size={16} style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', color: colors.primary.gold }} />
        )}
      </div>

      {/* Reste à Facturer */}
      <div
        style={{
          ...cardStyle,
          backgroundColor: resteAFacturer > 0 ? colors.background.lightRed : colors.background.lightGreen,
          border: `1.5px solid ${resteAFacturer > 0 ? colors.status.error : colors.status.success}`
        }}
        onClick={onShowDetail}
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
      >
        <div style={{
          ...iconContainerStyle,
          backgroundColor: resteAFacturer > 0 ? `${colors.status.error}20` : `${colors.status.success}20`
        }}>
          <Calculator size={20} color={resteAFacturer > 0 ? colors.status.error : colors.status.success} />
        </div>
        <div>
          <div style={{
            ...labelStyle,
            color: resteAFacturer > 0 ? colors.text.darkRed : colors.text.darkGreen
          }}>
            {resteAFacturer > 0 ? 'Reste à Facturer' : 'Tout est facturé'}
          </div>
          <div style={{
            ...valueStyle,
            color: resteAFacturer > 0 ? colors.status.error : colors.status.success
          }}>
            {formatEuro(Math.abs(resteAFacturer))}
            {resteAFacturer < 0 && ' (surplus)'}
          </div>
        </div>
        {clickable && (
          <ExternalLink size={16} style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', color: resteAFacturer > 0 ? colors.status.error : colors.status.success }} />
        )}
      </div>
    </div>
  );
}
